import { isProblemDetail } from '@services/base/apiService';
import { usePolisAlertsGetAlertvariabelen } from '@services/alerts-public/orval-api/polis-alerts/polis-alerts';
import {
  getStandaardAlertsGetByAdviseurQueryKey,
  useStandaardAlertsCreate,
  useStandaardAlertsUpdate,
} from '@services/alerts-public/orval-api/standaard-alerts/standaard-alerts';
import { AlertsFeaturesAlertAlertFrequentie as Frequentie } from '@services/alerts-public/orval-api/model/alertsFeaturesAlertAlertFrequentie';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { allVariablesInStringExist, maxLength, required } from '@shared/validation/vuelidate';
import { useQueryClient } from '@tanstack/vue-query';
import useVuelidate, { type ServerErrors } from '@vuelidate/core';
import { helpers, minLength } from '@vuelidate/validators';
import { isAxiosError } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { type StandaardAlert } from './types';

export const useStandaardAlertStore = () =>
  defineStore('standaardalertStore', () => {
    const notification = useNotification();
    const queryClient = useQueryClient();
    const externalValidations = ref<ServerErrors>({});
    const successCb = ref<() => void>(() => {});
    const queryKeyGetStandaardAlertsGetByAdviseur = getStandaardAlertsGetByAdviseurQueryKey();
    const defaultStandaardAlert = (): StandaardAlert => ({
      titel: '',
      inhoud: '',
      ontvangers: [],
      frequentie: Frequentie.Jaar,
    });

    const standaardAlert = ref<StandaardAlert>(defaultStandaardAlert());

    function resetStore() {
      standaardAlert.value = defaultStandaardAlert();
      externalValidations.value = {};
    }

    function handleError(e: any) {
      if (!isAxiosError(e)) return notification.error('Onbekende Fout');
      if (isProblemDetail(e.response)) {
        externalValidations.value = e.response.data.errors;
        return;
      }
      if (e.stack === '403')
        notification.error('Geen rechten om een standaard alert te maken of wijzigen');
      return notification.error(e.response?.data.title ?? 'Onbekende Fout');
    }

    function handleSuccesfullUpdate() {
      queryClient.invalidateQueries({ queryKey: [queryKeyGetStandaardAlertsGetByAdviseur] });
      successCb.value();
    }

    const standaardAlertCreate = useStandaardAlertsCreate({
      mutation: {
        onSuccess: () => handleSuccesfullUpdate(),
        onError: handleError,
      },
    });

    const standaardAlertUpdate = useStandaardAlertsUpdate({
      mutation: { onSuccess: () => handleSuccesfullUpdate(), onError: handleError },
    });

    const { data: alertVariabelen } = usePolisAlertsGetAlertvariabelen({
      query: {
        select: (data) => data.result,
      },
    });

    const alertsVarLabels = computed(
      () => alertVariabelen.value?.map((r) => r.userFriendlyName) ?? [],
    );

    const validateVariabelen = (value: string) =>
      allVariablesInStringExist(value, alertsVarLabels.value);

    const validators = {
      titel: {
        required,
        maxLength: maxLength(50),
      },
      inhoud: {
        required,
        maxLength: maxLength(4000),
        variabelenCheck: helpers.withMessage('Variabele niet gevonden.', validateVariabelen),
      },
      frequentie: { required },
      ontvangers: {
        minLength: helpers.withMessage('Er moet minimaal 1 ontvanger worden gekozen', minLength(1)),
      },
    };

    const validator = useVuelidate(validators as any, standaardAlert, {
      $externalResults: externalValidations,
    });

    const createStandaardAlert = (an: number | undefined, sa: StandaardAlert) => {
      standaardAlertCreate.mutate({
        data: {
          adviseurnummer: an,
          titel: sa.titel,
          ontvangers: sa.ontvangers,
          frequentie: sa.frequentie,
          inhoud: sa.inhoud,
        },
      });
    };

    const mutateStandaardAlert = (sa: StandaardAlert) => {
      standaardAlertUpdate.mutate({
        standaardAlertId: sa.id!,
        data: {
          titel: sa.titel,
          inhoud: sa.inhoud,
          ontvangers: sa.ontvangers.map((o) => ({ type: o })),
          frequentie: sa.frequentie,
        },
      });
    };
    async function saveStandaardAlert(StandaardAlertVoorAdviseurnummer: number | undefined) {
      externalValidations.value = {};
      const isValid = await validator.value.$validate();
      if (!isValid) return;
      if (!standaardAlert.value.id)
        return createStandaardAlert(StandaardAlertVoorAdviseurnummer, standaardAlert.value);
      return mutateStandaardAlert(standaardAlert.value);
    }

    return {
      standaardAlert,
      validator,
      successCb,
      saveStandaardAlert,
      reset: resetStore,
      isSavingAlerts: computed(
        () => standaardAlertCreate.isPending.value || standaardAlertUpdate.isPending.value,
      ),
    };
  })();
